/**
 * Health tracker — pure helpers. The capstone Personal discipline: a curated
 * vitals list (logged over time) and a daily check-in (appointments are calendar
 * EVENTS — utils/appointment.ts). XP comes
 * from the daily check-in (plus partial credit for logging vitals) — daily-capped
 * and farm-proof. `sumHealthXP` is the single definition crediting Personal once
 * + the Health area rank. Readings carry a `source` so wearable auto-import
 * (Apple HealthKit, etc.) can append later without changing the model.
 */

export interface VitalDef {
  id: string;
  name: string;
  unit: string;
  icon: string;
  /** Healthy-range bounds; null = unbounded on that side; both null = untargeted. */
  targetMin: number | null;
  targetMax: number | null;
  active: boolean;
}

export interface VitalReading {
  id: string;
  vitalId: string;
  value: number;
  source: string; // 'manual' | 'healthkit' | …
  recordedAt: string; // ISO
}

export interface Symptom {
  name: string;
  severity: number; // 1..3 (mild/moderate/severe)
}

export interface CheckIn {
  date: string; // local YYYY-MM-DD
  mood: number | null; // 1..5
  energy: number | null; // 1..5
  symptoms: Symptom[];
  note: string;
}

export type Recurrence = 'none' | 'monthly' | 'quarterly' | 'yearly';

import { POINTS, TRACKER_PARAMS } from './levelCalculation';

/** Icon keys for the vitals picker (mapped to lucide in the UI). */
export const HEALTH_ICON_KEYS = [
  'scale', 'heart', 'heartpulse', 'activity', 'droplet', 'thermometer', 'gauge',
  'brain', 'wind', 'zap', 'footprints', 'flame', 'ruler', 'percent', 'stethoscope', 'moon',
] as const;

export const DEFAULT_VITAL_ICON = 'activity';

export const DAILY_HEALTH_XP = 50;

export const DEFAULT_VITALS: VitalDef[] = [
  { id: 'vital-weight', name: 'Weight', unit: 'kg', icon: 'scale', targetMin: null, targetMax: null, active: true },
  { id: 'vital-rhr', name: 'Resting HR', unit: 'bpm', icon: 'heart', targetMin: null, targetMax: 60, active: true },
  { id: 'vital-hrv', name: 'HRV', unit: 'ms', icon: 'activity', targetMin: 50, targetMax: null, active: true },
  { id: 'vital-spo2', name: 'Blood O₂', unit: '%', icon: 'droplet', targetMin: 95, targetMax: 100, active: true },
  { id: 'vital-sys', name: 'Systolic BP', unit: 'mmHg', icon: 'gauge', targetMin: null, targetMax: 120, active: true },
  { id: 'vital-dia', name: 'Diastolic BP', unit: 'mmHg', icon: 'gauge', targetMin: null, targetMax: 80, active: true },
];

export const RECURRENCE_LABELS: Record<Recurrence, string> = {
  none: 'One-time',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly',
};

export type VitalStatus = 'in' | 'out' | 'none';

/** Is a value within the vital's healthy range? 'none' when the vital is untargeted. */
export function vitalStatus(value: number, def: Pick<VitalDef, 'targetMin' | 'targetMax'>): VitalStatus {
  if (def.targetMin == null && def.targetMax == null) return 'none';
  const okMin = def.targetMin == null || value >= def.targetMin;
  const okMax = def.targetMax == null || value <= def.targetMax;
  return okMin && okMax ? 'in' : 'out';
}

/** Latest reading (max recordedAt) from a list, or null. */
export function latestReading(readings: ReadonlyArray<VitalReading>): VitalReading | null {
  let best: VitalReading | null = null;
  for (const r of readings) {
    if (!best || r.recordedAt > best.recordedAt) best = r;
  }
  return best;
}

/** Economy XP for one day: a check-in is the full ritual; vitals-only earns a ratio. */
export function dayHealthXP(checkedIn: boolean, vitalsLogged: number): number {
  // Config-backed (console → XP economy); the consts are the defaults.
  const cap = POINTS.TRACKER_HEALTH_DAILY ?? DAILY_HEALTH_XP;
  if (checkedIn) return Math.round(cap);
  return vitalsLogged > 0 ? Math.round(cap * TRACKER_PARAMS.healthVitalsRatio) : 0;
}

/** Total Health economy XP across days (single source of truth). */
export function sumHealthXP(days: ReadonlyArray<{ checkedIn: boolean; vitalsLogged: number }>): number {
  return days.reduce((sum, d) => sum + dayHealthXP(d.checkedIn, d.vitalsLogged), 0);
}

// ⚠️ There used to be a vitals-only / check-in-only split here: the check-in
// lived in Personal → Mind and took the full-cap days while Health kept the
// vitals-only days. On 2026-09-30 the check-in moved into the period tracker
// (in Health), so `sumHealthXP` is the whole of it again and the split is gone.

/** Consecutive days with a check-in, ending at today (today optional, doesn't break). */
export function healthStreak(
  checkedInDates: ReadonlyArray<string>,
  todayStr: string,
  shift: (ymd: string, n: number) => string
): number {
  const set = new Set(checkedInDates);
  let streak = 0;
  let cursor = set.has(todayStr) ? todayStr : shift(todayStr, -1);
  while (set.has(cursor)) {
    streak++;
    cursor = shift(cursor, -1);
  }
  return streak;
}

/** Fraction (0..100) of targeted vitals whose latest reading is in range. */
export function vitalsInRangePct(
  vitals: ReadonlyArray<VitalDef>,
  latestByVital: (vitalId: string) => VitalReading | null
): number | null {
  const targeted = vitals.filter(v => v.active && (v.targetMin != null || v.targetMax != null));
  let counted = 0;
  let inRange = 0;
  for (const v of targeted) {
    const r = latestByVital(v.id);
    if (!r) continue;
    counted++;
    if (vitalStatus(r.value, v) === 'in') inRange++;
  }
  if (counted === 0) return null; // no data → excluded from the score
  return Math.round((inRange / counted) * 100);
}
